import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslate } from '../../hooks/useTranslate';
import { TRAINING_MESSAGES } from '../../constants/training';
import { getTrainingVideoUrl } from '../../services/trainingVideoService';
import type { TrainingVideo } from '../../types/training';

interface InlineVideoPlayerProps {
    videoId: string | null;
    onVideoMetaLoaded?: (meta: TrainingVideo) => void;
}

const InlineVideoPlayer: React.FC<InlineVideoPlayerProps> = ({ videoId, onVideoMetaLoaded }) => {
    const { t } = useTranslate();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [paused, setPaused] = useState(true);

    useEffect(() => {
        if (!videoId) {
            setVideoUrl(null);
            setError(null);
            setPaused(true);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        setPaused(true);

        getTrainingVideoUrl(videoId)
            .then(({ url, video }) => {
                if (!cancelled) {
                    setVideoUrl(url);
                    setLoading(false);
                    onVideoMetaLoaded?.(video);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError('Failed to load video');
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId]);

    const togglePlay = useCallback(() => {
        const vid = videoRef.current;
        if (!vid) return;
        if (vid.paused) {
            vid.play();
        } else {
            vid.pause();
        }
    }, []);

    // Empty state — no video selected
    if (!videoId) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-gray-900 rounded-xl">
                <div className="text-center space-y-4">
                    <div className="w-20 h-20 mx-auto rounded-full bg-white/10 flex items-center justify-center">
                        <svg className="w-10 h-10 text-white/40" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </div>
                    <p className="text-white/50 text-sm">{t(TRAINING_MESSAGES.SELECT_VIDEO)}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-gray-900 rounded-xl overflow-hidden">
            <div className="relative aspect-video bg-black">
                {loading ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60 text-sm gap-3">
                        <div className="w-10 h-10 border-3 border-white/20 border-t-white/70 rounded-full animate-spin" />
                        {t(TRAINING_MESSAGES.LOADING_VIDEOS)}
                    </div>
                ) : error ? (
                    <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm">
                        {t(error)}
                    </div>
                ) : videoUrl ? (
                    <>
                        <video
                            ref={videoRef}
                            key={videoUrl}
                            className="absolute inset-0 w-full h-full object-contain"
                            controls
                            controlsList="nodownload"
                            disablePictureInPicture
                            onContextMenu={e => e.preventDefault()}
                            onPlay={() => setPaused(false)}
                            onPause={() => setPaused(true)}
                            src={videoUrl}
                        >
                            {t('Your browser does not support the video element.')}
                        </video>
                        {/* Large centered play overlay when paused */}
                        {paused && (
                            <button
                                type="button"
                                onClick={togglePlay}
                                className="absolute inset-0 z-10 flex items-center justify-center bg-black/20 transition-opacity hover:bg-black/30 cursor-pointer"
                                aria-label={t('Play')}
                            >
                                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                                    <svg className="w-8 h-8 text-[#D31145] ml-1" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                </div>
                            </button>
                        )}
                    </>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
                        <svg className="w-12 h-12 text-white/40 mr-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                        {t('Video not available')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InlineVideoPlayer;
